import { PlusOutlined } from "@ant-design/icons";
import { contraceptionApi } from "@api";
import {
  ComponentPagination,
  SearchFilter,
  SelectedFilter,
  SharedButton,
  StyledListPage,
  ComponentHeader,
  StyledInnerListPage,
  SharedIconButton,
  PlusIcon,
  DownloadIcon
} from "@components";

import { PATH_CREATE_DEPO_RECORDS, contraceptionFilter, enumFilter, initPage } from "@configs";
import { IClinic, IPagination } from "@interfaces";

import { selectApp, setLoading } from "@redux";
import { useNotify } from "@utils";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Tooltip } from "antd";
import { RecordsTable } from "@modules";
import { getTransformArrayToObject } from "@modules/records/contraceptionHelper";

export const PageRecords = () => {
  // hook
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { success, error } = useNotify();
  const { mobile } = useSelector(selectApp);
  // state
  const [page, setPage] = useState<IPagination>(initPage);
  const [total, setTotal] = useState<number>(0);
  const [data, setData] = useState<IClinic[]>([]);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState<string>("");
  const [date, setDate] = useState<string[]>([]);

  useEffect(() => {
    fetchContraceptions();
  }, [page, text, date]);

  const fetchContraceptions = async () => {
    try {
      dispatch(setLoading(true));
      const res = await contraceptionApi.contraceptions({
        page: page.page,
        limit: page.limit,
        searchColumn: filters,
        search: text,
        startDate: date[0],
        endDate: date[1],
      });
      let updatedRecords = JSON.parse(JSON.stringify(res.data?.data));
      updatedRecords = updatedRecords.map((record: any, index: number) => {
        const clientData = getTransformArrayToObject(record.contraception_clients);
        record.contraception_clients = clientData;
        return record;
      });
      setData(updatedRecords);
      setTotal(res.data?.nTotal);
      success(t("message.fetch.success"));
    } catch (err: any) {
      error(t("message.fetch.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const handleDateFilter = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
    setDate(dateStrings);
  };
  const handlePagination = (current: number, size: number) => {
    console.log(current);
    setPage({ ...page, page: current, limit: size });
  };

  const goToCreate = () => history.push(PATH_CREATE_DEPO_RECORDS);
  const changeFilter = (value: string) => setFilters(value);
  const searchText = (value: string) => setText(value);
  const handleClearSearch = () => {
    setText("");
    setFilters("");
  };
  const handleClearDate = () => setDate([]);

  const handleDownloadCsv = async () => {
    try {
      const { data } = await contraceptionApi.exportExcel({
        searchColumn: filters,
        search: text,
        startDate: date[0],
        endDate: date[1],
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: `text/csv` }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Exported depo record");
      link.click();
      window.URL.revokeObjectURL(url);
      success(t("message.download.success"));
    } catch (err: any) {
      error(t("message.download.fail"));
      throw new Error(err);
    }
  };

  const button = (
    <>
    <Tooltip title={t("page.download_excel")}>
      <SharedButton
        type="primary"
        ghost
        icon={<DownloadIcon />}
        size="middle"
        onClick={handleDownloadCsv}
      >
        {t("page.download")}
      </SharedButton>
    </Tooltip>
    <Tooltip title={t("page.add_patient_record")}>
      {mobile ? (
        <SharedIconButton type="primary" ghost icon={<PlusIcon />} onClick={goToCreate} />
      ) : (
        <SharedButton
          type="primary"
          ghost
          icon={<PlusOutlined />}
          size="middle"
          onClick={goToCreate}
        >
          {t("page.add_patient_record")}
        </SharedButton>
      )}
    </Tooltip>
    </>
  );

  return (
    <>
      <ComponentHeader title={t("page.depo_and_ocpills_records")} />
      <StyledListPage>
        <StyledInnerListPage>
          <SearchFilter
            actionBtn={button}
            filter={contraceptionFilter}
            callbackFun={changeFilter}
            onDateChange={handleDateFilter}
            searchText={searchText}
            searchInputPlaceholder={t("page.depo_record_search_placeholder")}
            selectedOption={filters}
          />
          <SelectedFilter
            handleClearSearch={handleClearSearch}
            handleClearDate={handleClearDate}
            selected={filters}
            formDate={date[0]}
            toDate={date[1]}
          />
          <RecordsTable data={data} callback={fetchContraceptions} />
          <ComponentPagination total={total} page={page.page} changePage={handlePagination} />
        </StyledInnerListPage>
      </StyledListPage>
    </>
  );
};
